import { supabase } from '../lib/supabase';

export interface ContractLock {
  id: string;
  customer_id: string;
  is_locked: boolean;
  locked_by: string | null;
  locked_at: string | null;
  unlocked_by: string | null;
  unlocked_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserRole {
  id: string;
  user_id: string;
  role: 'admin' | 'editor' | 'viewer';
  created_at: string;
  updated_at: string;
}

export interface UnlockRequest {
  id: string;
  customer_id: string;
  requested_by: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
  updated_at: string;
}

export const contractLockService = {
  async getContractLock(customerId: string): Promise<ContractLock | null> {
    const { data, error } = await supabase
      .from('contract_locks')
      .select('*')
      .eq('customer_id', customerId)
      .maybeSingle();

    if (error) {
      console.error('Erro ao buscar bloqueio:', error);
      return null;
    }

    return data;
  },

  async isContractLocked(customerId: string): Promise<boolean> {
    const lock = await this.getContractLock(customerId);
    return lock?.is_locked ?? true;
  },

  async getUserRole(userId?: string): Promise<UserRole | null> {
    const effectiveUserId = userId || (await supabase.auth.getUser()).data.user?.id;

    if (!effectiveUserId) return null;

    const { data, error } = await supabase
      .from('user_roles')
      .select('*')
      .eq('user_id', effectiveUserId)
      .maybeSingle();

    if (error) {
      console.error('Erro ao buscar role:', error);
      return null;
    }

    return data;
  },

  async canEditContract(customerId: string): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const role = await this.getUserRole(user.id);

    if (role?.role === 'admin') {
      return true;
    }

    const isLocked = await this.isContractLocked(customerId);
    return !isLocked;
  },

  async logAccess(
    action: 'view' | 'edit_attempt' | 'unlock_request' | 'unlock_approved' | 'unlock_rejected' | 'lock' | 'unlock',
    customerId: string | null,
    fieldName?: string,
    metadata?: Record<string, any>
  ): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    await supabase.from('contract_access_logs').insert({
      customer_id: customerId,
      user_id: user.id,
      action,
      field_name: fieldName,
      metadata: metadata || {}
    });
  },

  async requestUnlock(customerId: string, reason: string): Promise<{ success: boolean; error?: string }> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Usuário não autenticado' };
    }

    const { error } = await supabase
      .from('unlock_requests')
      .insert({
        customer_id: customerId,
        requested_by: user.id,
        reason,
        status: 'pending'
      });

    if (error) {
      console.error('Erro ao solicitar desbloqueio:', error);
      return { success: false, error: error.message };
    }

    await this.logAccess('unlock_request', customerId, undefined, { reason });

    return { success: true };
  },

  async getPendingUnlockRequests(): Promise<UnlockRequest[]> {
    const { data, error } = await supabase
      .from('unlock_requests')
      .select('*')
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erro ao buscar solicitações:', error);
      return [];
    }

    return data || [];
  },

  async approveUnlockRequest(
    requestId: string,
    reviewNotes?: string
  ): Promise<{ success: boolean; error?: string }> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Usuário não autenticado' };
    }

    const role = await this.getUserRole(user.id);
    if (role?.role !== 'admin') {
      return { success: false, error: 'Apenas administradores podem aprovar solicitações' };
    }

    const { data: request, error: fetchError } = await supabase
      .from('unlock_requests')
      .select('*')
      .eq('id', requestId)
      .maybeSingle();

    if (fetchError || !request) {
      return { success: false, error: 'Solicitação não encontrada' };
    }

    const { error: updateError } = await supabase
      .from('unlock_requests')
      .update({
        status: 'approved',
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString(),
        review_notes: reviewNotes
      })
      .eq('id', requestId);

    if (updateError) {
      return { success: false, error: updateError.message };
    }

    const { error: lockError } = await supabase
      .from('contract_locks')
      .update({
        is_locked: false,
        unlocked_by: user.id,
        unlocked_at: new Date().toISOString()
      })
      .eq('customer_id', request.customer_id);

    if (lockError) {
      return { success: false, error: lockError.message };
    }

    await this.logAccess('unlock_approved', request.customer_id, undefined, { request_id: requestId });

    return { success: true };
  },

  async rejectUnlockRequest(
    requestId: string,
    reviewNotes: string
  ): Promise<{ success: boolean; error?: string }> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Usuário não autenticado' };
    }

    const role = await this.getUserRole(user.id);
    if (role?.role !== 'admin') {
      return { success: false, error: 'Apenas administradores podem rejeitar solicitações' };
    }

    const { data: request, error: fetchError } = await supabase
      .from('unlock_requests')
      .select('*')
      .eq('id', requestId)
      .maybeSingle();

    if (fetchError || !request) {
      return { success: false, error: 'Solicitação não encontrada' };
    }

    const { error } = await supabase
      .from('unlock_requests')
      .update({
        status: 'rejected',
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString(),
        review_notes: reviewNotes
      })
      .eq('id', requestId);

    if (error) {
      return { success: false, error: error.message };
    }

    await this.logAccess('unlock_rejected', request.customer_id, undefined, { request_id: requestId });

    return { success: true };
  },

  async lockContract(customerId: string): Promise<{ success: boolean; error?: string }> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Usuário não autenticado' };
    }

    const role = await this.getUserRole(user.id);
    if (role?.role !== 'admin') {
      return { success: false, error: 'Apenas administradores podem bloquear contratos' };
    }

    const { error } = await supabase
      .from('contract_locks')
      .update({
        is_locked: true,
        locked_by: user.id,
        locked_at: new Date().toISOString()
      })
      .eq('customer_id', customerId);

    if (error) {
      return { success: false, error: error.message };
    }

    await this.logAccess('lock', customerId);

    return { success: true };
  }
};
